import type { VerySlimCarrier } from '../models/carrier.ts'
import type { Workflow } from '../models/common.ts'
import type { Carrier, CarrierStatus, CarrierType } from './carrier.ts'

export type GetCarrierTypeInput = {
  status: CarrierStatus
  ltl?: { isApiEnabled?: boolean }
  parcel?: { isApiEnabled?: boolean }
  truckload?: { rating?: { supportsAPIRating?: boolean } }
  isAvailableToAllAccounts?: boolean
  needsAccountProvisioning?: boolean
}

type SupportsCustomerRoutedInput = {
  status: CarrierStatus
  supportsParcelRates?: boolean
  allowCustomerRoutedShipment?: boolean
}

type ToVerySlimCarrierInput = Pick<
  Carrier,
  '_id' | 'name' | 'code' | 'supportsManualRates'
>

export const carrierFuncs = {
  toVerySlimCarrier(carrier: ToVerySlimCarrierInput): VerySlimCarrier {
    return {
      id: carrier._id.toHexString(),
      name: carrier.name?.trim(),
      code: carrier.code,
      supportsManualRates: carrier.supportsManualRates,
    }
  },
  supportsCustomerRouted(
    carrier: SupportsCustomerRoutedInput,
    workflow: Workflow,
  ): boolean {
    if (carrier.status === 'removed') return false
    if (workflow === 'truckload') {
      return true
    }
    if (workflow === 'parcel') {
      return !!carrier.supportsParcelRates
    }
    if (workflow === 'ltl') {
      return !!carrier.allowCustomerRoutedShipment
    }
    return false
  },
  supportsApiQuoting(carrier: GetCarrierTypeInput): boolean {
    return !!(
      carrier.ltl?.isApiEnabled ||
      carrier.parcel?.isApiEnabled ||
      carrier.truckload?.rating?.supportsAPIRating
    )
  },
  carrierNeedsProvisioning(carrier: GetCarrierTypeInput): boolean {
    return !!carrier.needsAccountProvisioning
  },
  hasApiCapabilities(carrier: GetCarrierTypeInput): boolean {
    return (
      carrierFuncs.supportsApiQuoting(carrier) ||
      carrierFuncs.carrierNeedsProvisioning(carrier)
    )
  },
  isApiEnabled(carrier: GetCarrierTypeInput): boolean {
    return carrierFuncs.supportsApiQuoting(carrier)
  },
  getCarrierType(carrier: GetCarrierTypeInput): CarrierType {
    if (carrier.status === 'removed') return 'removed'
    if (carrierFuncs.supportsApiQuoting(carrier)) return 'api-integrated'
    if (carrier.isAvailableToAllAccounts) return 'global-spot'
    return 'restricted'
  },
  normalizeScac(scac?: string): string | undefined {
    return scac?.toLowerCase()?.trim()
  },
  normalizeCarrierName(name?: string): string | undefined {
    return name?.trim()
  },
}
